import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Alert } from "@mui/material";
import { Download } from "react-feather";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { downloadFile, formatGBP2, isSuspended, refreshBilling, saveBilling } from "../../components/Features";
import styles from "../../components/billing.module.css";
import Loader from "../../components/Loader";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";

// Where blocked companies land: suspended for an unpaid invoice (service returns when it is
// paid), or subscription ended after unsubscribing (the admin can resubscribe here).
const AccountSuspended = () => {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(() => authData());
  const API_TOKEN = auth?.api_token;
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [payment, setPayment] = useState(null);
  const [stillSuspended, setStillSuspended] = useState(isSuspended(auth));
  const [resubOpen, setResubOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const ended = auth?.subscription?.status === "cancelled";

  const { websiteData } = useWebsite();
  useSiteBranding(ended ? "Subscription ended" : "Service suspended", websiteData);

  useEffect(() => {
    refreshBilling().then((fresh) => {
      if (fresh) setAuth(fresh);
      setStillSuspended(isSuspended(fresh));
    });
    axios.get(process.env.REACT_APP_API_URL + "v1/billing/invoices", {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setInvoices(response.data.data.filter((i) => i.status === "pending" || i.status === "overdue"));
        setPayment(response.data.payment);
      }
    }).catch(() => setLoading(false));
  }, [API_TOKEN]);

  const resubscribe = () => {
    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/billing/resubscribe", {}, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        saveBilling(response.data.data);
        navigate("/company-profile?tab=addons", { replace: true });
      } else {
        setResubOpen(false);
        setError(response.data.message);
      }
    }).catch(() => {
      setSaving(false);
      setResubOpen(false);
      setError("Could not restart your subscription. Please try again.");
    });
  };

  if (!stillSuspended) {
    return <Navigate to={"/dashboard"} replace />;
  }

  const total = invoices.reduce((sum, i) => sum + Number(i.total || 0), 0);
  const isAdmin = auth?.user_type === 2;
  const manual = auth?.subscription?.suspended_reason === "manual";

  return (<div className={`${styles.SuspendedWrap}`}>
    <Loader open={loading} setOpen={setLoading} />

    <div className={`${styles.SuspendedHead}`}>
      {ended ? (<>
        <h2>Your subscription has ended</h2>
        <p>
          {auth?.subscription?.cancels_at ? `Your subscription ended on ${dayjs(auth.subscription.cancels_at).format("DD MMM YYYY")}. ` : ""}
          Bookings, the dashboard and the driver and customer apps are no longer available. Your data is kept safe.
          {isAdmin ? " You can resubscribe at any time to pick up exactly where you left off." : " Please contact your company administrator to resubscribe."}
        </p>
      </>) : (<>
        <h2>Your service is suspended</h2>
        <p>
          {manual
            ? "Your account has been suspended by the platform administrator. Please get in touch to restore access."
            : "We have not received payment for the invoice(s) below within the 7-day grace period, so bookings, the dashboard and the driver, customer and warehouse apps are paused. Everything is restored automatically as soon as payment is recorded. Your data is safe."}
        </p>
      </>)}
    </div>

    {invoices.length > 0 && <div className={`${styles.Panel}`}>
      <p className={`${styles.PanelTitle}`}>Outstanding invoices · {formatGBP2(total)}</p>
      {invoices.map((inv) => (<div className={`${styles.InvoiceRow}`} key={inv.id}>
        <strong>{inv.invoice_number}</strong>
        <span className={`${styles.Muted}`}>Due {dayjs(inv.due_date).format("DD MMM YYYY")}</span>
        <span className={`${styles.InvoiceRowAmount}`}>{formatGBP2(inv.total)}</span>
        <button type="button" className={`${styles.SecondaryBU}`} onClick={() => downloadFile(`v1/billing/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`)}><Download size={16} />PDF</button>
      </div>))}
    </div>}

    {(!ended || invoices.length > 0) && <div className={`${styles.Panel}`}>
      <p className={`${styles.PanelTitle}`}>How to pay</p>
      {payment?.instructions
        ? <p className={`${styles.Pre}`}>{payment.instructions}</p>
        : <p className={`${styles.Muted}`}>Please contact us to arrange payment.</p>}
      {invoices.length > 0 && <p className={`${styles.Muted}`} style={{ marginTop: 10 }}>Use the invoice number as your payment reference.</p>}
      {payment?.billing_email && <p className={`${styles.Muted}`} style={{ marginTop: 10 }}>Questions? Email <a href={`mailto:${payment.billing_email}`}>{payment.billing_email}</a>.</p>}
    </div>}

    <div className={`${styles.GateActions}`} style={{ justifyContent: "flex-start" }}>
      {ended && isAdmin && <button type="button" className={`${styles.PrimaryBU}`} onClick={() => setResubOpen(true)}>Resubscribe</button>}
      {isAdmin && <Link to={"/company-profile?tab=billing"} className={ended ? `${styles.SecondaryBU}` : `${styles.PrimaryBU}`}>Billing &amp; invoices</Link>}
      <Link to={"/my-profile"} className={`${styles.SecondaryBU}`}>My profile</Link>
    </div>

    <Dialog open={resubOpen} onClose={() => !saving && setResubOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Resubscribe?</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: 14, lineHeight: "22px", color: "#4b5563", margin: 0 }}>
          Your account and data come back straight away. You are billed monthly from today for the Basic plan plus any add-ons you switch on, with your first invoice emailed a few days before {dayjs().add(1, "month").format("DD MMM YYYY")}. Add-ons start switched off; turn on the ones you need next.
        </p>
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 20px" }}>
        <button type="button" className={`${styles.SecondaryBU}`} disabled={saving} onClick={() => setResubOpen(false)}>Cancel</button>
        <button type="button" className={`${styles.PrimaryBU}`} disabled={saving} onClick={resubscribe}>{saving ? "Restarting…" : "Resubscribe"}</button>
      </DialogActions>
    </Dialog>

    <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError("")} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
      <Alert onClose={() => setError("")} severity="error" variant="filled" sx={{ width: "100%" }}>{error}</Alert>
    </Snackbar>
  </div>);
}

export default withAdminAuth(AccountSuspended);
